import { signalStore, withMethods, withState } from '@ngrx/signals';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { inject } from '@angular/core';
import { WireApiService } from '../services';
import { logger } from '../utils';

type FaviconState = {
  currentFaviconUrl: string | null;
  hasCustomFavicon: boolean;
  loading: boolean;
  currentTenantId: string | null;
};

const initialState: FaviconState = {
  currentFaviconUrl: null,
  hasCustomFavicon: false,
  loading: false,
  currentTenantId: null,
};

export type FaviconStore = InstanceType<typeof FaviconStore>;

export const FaviconStore = signalStore(
  { providedIn: 'root' },
  withDevtools('favicon-store'),
  withState(initialState),
  withMethods((store) => {
    const wireApiService = inject(WireApiService);

    return {
      setLoading(loading: boolean): void {
        updateState(store, 'Set loading', { loading });
      },
      async loadCurrentFavicon(): Promise<void> {
        const tenantId = store.currentTenantId();
        if (tenantId) {
          await this.updateFavicon(tenantId);
        }
      },
      async updateFavicon(tenantId: string): Promise<void> {
        try {
          updateState(store, 'Set loading and tenant', {
            loading: true,
            currentTenantId: tenantId,
          });
          this.clearAllFavicons();
          const response = await wireApiService.tenantFavIconProviderApi.getTenantFavIconRaw({
            tenantId: tenantId,
          });
          if (response.raw.status === 200) {
            const blob = await response.raw.blob();

            const faviconDataUrl = await this.blobToDataUrl(blob);

            this.updateFaviconInDOM(faviconDataUrl);

            updateState(store, 'Set custom favicon', {
              currentFaviconUrl: faviconDataUrl,
              hasCustomFavicon: true,
              loading: false,
            });

            logger.info('[FaviconStore] Custom favicon updated successfully');
          } else if (response.raw.status === 404) {
            logger.info('[FaviconStore] No custom favicon found, using default');
            this.useDefaultFavicon();
          }
        } catch (error) {
          logger.warn('[FaviconStore] Failed to fetch favicon, using default:', error);
          this.useDefaultFavicon();
        }
      },
      useDefaultFavicon(): void {
        const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
        existingFavicons.forEach((link) => link.remove());

        const defaultFavicon = document.createElement('link');
        defaultFavicon.rel = 'icon';
        defaultFavicon.type = 'image/x-icon';
        defaultFavicon.href = 'favicon.ico';
        document.head.appendChild(defaultFavicon);

        const shortcutIcon = document.createElement('link');
        shortcutIcon.rel = 'shortcut icon';
        shortcutIcon.type = 'image/x-icon';
        shortcutIcon.href = 'favicon.ico';
        document.head.appendChild(shortcutIcon);

        updateState(store, 'Set default favicon', {
          currentFaviconUrl: null,
          hasCustomFavicon: false,
          loading: false,
        });

        logger.info('[FaviconStore] Using default favicon');
      },

      resetToDefault(): void {
        updateState(store, 'Reset to default', {
          currentTenantId: null,
        });
        this.useDefaultFavicon();
      },

      clearAllFavicons(): void {
        const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
        existingFavicons.forEach((link) => link.remove());
      },
      blobToDataUrl(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      },

      updateFaviconInDOM(faviconUrl: string): void {
        const existingFavicons = document.querySelectorAll('link[rel*="icon"]');

        const newFavicon = document.createElement('link');
        newFavicon.rel = 'icon';
        newFavicon.type = this.getFaviconType(faviconUrl);
        newFavicon.href = faviconUrl;

        const shortcutIcon = document.createElement('link');
        shortcutIcon.rel = 'shortcut icon';
        shortcutIcon.type = this.getFaviconType(faviconUrl);
        shortcutIcon.href = faviconUrl;

        document.head.appendChild(newFavicon);
        document.head.appendChild(shortcutIcon);

        existingFavicons.forEach((link) => {
          if (link !== newFavicon && link !== shortcutIcon) {
            link.remove();
          }
        });
      },
      getFaviconType(faviconUrl: string): string {
        if (faviconUrl.startsWith('data:image/png')) {
          return 'image/png';
        } else if (faviconUrl.startsWith('data:image/svg')) {
          return 'image/svg+xml';
        } else if (faviconUrl.includes('.png')) {
          return 'image/png';
        } else if (faviconUrl.includes('.svg')) {
          return 'image/svg+xml';
        }
        return 'image/x-icon';
      },
    };
  })
);
